import React, { memo, useMemo } from "react";
import { Fragment } from "react";
import { ReactComponent as DirhamSymbol } from "../../../assets/svgs/Dirham Currency Symbol - Black.svg";
const PriceDisplay = memo(
  ({
    product,
    currency,
    finalProductPrice,
    finalDiscountedPrice,
    numberWithCommas,
    getFirstVariantPrice,
    getDiscountPrice,
    t,
    variant = "default", // 'default', 'mobile', 'with-discount'
    className = "",
    showStartsFrom = true,
    isRTL = false, // Now accepts boolean, string, or detects from currency
    style = {},
    displaySingleLine,
  }) => {
    // Handle RTL detection - support multiple input formats
    const isRightToLeft = useMemo(() => {
      if (typeof isRTL === "boolean") return isRTL;
      if (typeof isRTL === "string") return isRTL === "rtl";
      // Fallback: detect from currency direction or document
      if (currency?.direction) return currency.direction === "rtl";
      return document.dir === "rtl";
    }, [isRTL, currency]);

    // Default helper functions if not provided
    const safeNumberWithCommas =
      numberWithCommas ||
      ((num) => {
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      });

    const safeGetFirstVariantPrice =
      getFirstVariantPrice ||
      ((variations) => {
        const firstCategory = Object.values(variations)[0];
        const firstVariant = Object.values(firstCategory)[0];
        if (firstVariant.childern && firstVariant.childern.length > 0) {
          return firstVariant.childern[0].price;
        }
        return firstVariant.price;
      });

    const safeGetDiscountPrice =
      getDiscountPrice ||
      ((price, discount) => {
        if (!discount) return price;
        if (discount.discount_type === "percentage") {
          return price - (price * discount.discount_amount) / 100;
        }
        return price - discount.discount_amount;
      });

    const safeT = t || ((key) => key);

    // Memoized calculations to prevent unnecessary re-computations
    const priceData = useMemo(() => {
      const hasVariations =
        product.variations && Object.values(product.variations).length > 0;
      const isVariationPricing = hasVariations && product.price === "0";

      const originalPrice = isVariationPricing
        ? safeGetFirstVariantPrice(product.variations)
        : finalProductPrice;

      const discountedPrice = isVariationPricing
        ? safeGetDiscountPrice(
            safeGetFirstVariantPrice(product.variations),
            product.discount
          )
        : finalDiscountedPrice;

      const formattedOriginalPrice = safeNumberWithCommas(originalPrice);
      const formattedDiscountedPrice = safeNumberWithCommas(discountedPrice);

      const hasDiscount = originalPrice !== discountedPrice && product.discount;
      const shouldShowStartsFrom = isVariationPricing && showStartsFrom;

      return {
        originalPrice,
        discountedPrice,
        formattedOriginalPrice,
        formattedDiscountedPrice,
        hasDiscount,
        shouldShowStartsFrom,
        isVariationPricing,
      };
    }, [
      product,
      finalProductPrice,
      finalDiscountedPrice,
      safeGetFirstVariantPrice,
      safeGetDiscountPrice,
      safeNumberWithCommas,
      showStartsFrom,
    ]);

    // Format price with currency based on language direction
    const formatPriceWithCurrency = (price) => {
      const currencySymbol = currency.currencySymbol;

      // Special handling for UAE Dirham
      const renderCurrencySymbol =
        currencySymbol === "AED" ? (
          <DirhamSymbol className=" dirham-icon" />
        ) : (
          currencySymbol
        );

      if (isRightToLeft) {
        // Arabic format: 429 [AED Symbol]
        return (
          <>
            {price} {renderCurrencySymbol}
          </>
        );
      } else {
        // English format: [AED Symbol] 429
        return (
          <>
            {renderCurrencySymbol} {price}
          </>
        );
      }
    };

    // Render starts from text
    const renderStartsFrom = () => {
      if (!priceData.shouldShowStartsFrom) return null;

      return <span className="starts-from-text">{safeT("starts_from")} </span>;
    };

    // Simple single price display (no discount)
    if (!priceData.hasDiscount) {
      return (
        <span
          className={`price-display simple-price ${className}`}
          style={style}
        >
          {renderStartsFrom()}
          <span className="mobile-price bold">
            {formatPriceWithCurrency(priceData.formattedOriginalPrice)}
          </span>
        </span>
      );
    }

    // Discount price display
    return (
      <div
        className={`price-display discount-variant ${className}`}
        style={style}
      >
        {!displaySingleLine && (
          <div className={`d-flex ${!displaySingleLine && "flex-column"}`}>
            {/* Original price (crossed out) */}
            <span className="old mr-5 ml-5">
              <span className="original-price crossed-out">
                {formatPriceWithCurrency(priceData.formattedOriginalPrice)}
              </span>
            </span>

            {/* Discounted price */}
            <span className="mobile-price bold text-danger">
              {renderStartsFrom()}
              <span>
                {formatPriceWithCurrency(priceData.formattedDiscountedPrice)}
              </span>
            </span>
          </div>
        )}

        {/* Renders Single Line for product pages and other */}

        {displaySingleLine && (
          <div className={`d-flex ${!displaySingleLine && "flex-column"}`}>
            {/* Discounted price */}
            <span className="mobile-price bold text-danger">
              {renderStartsFrom()}
              <span>
                {formatPriceWithCurrency(priceData.formattedDiscountedPrice)}
              </span>
            </span>

            {/* Original price (crossed out) */}
            <span className="old mr-5 ml-5">
              <span className="original-price crossed-out">
                {formatPriceWithCurrency(priceData.formattedOriginalPrice)}
              </span>
            </span>
          </div>
        )}
      </div>
    );
  }
);

PriceDisplay.displayName = "PriceDisplay";

export default PriceDisplay;
